import { useCallback, useMemo, useState } from "react";
import { sendOTPRequest, verifyOTP } from "utils/api";
import { getApiErrorMessage } from "utils/apiError";
import i18n from "i18n";
import { getPostLoginPath, versionedPath } from "utils/routes";
import { clearRememberedTrackingAuth } from "utils/trackingAuth";

function useOtpFlow({
  response,
  setResponse,
  encipherencode,
  params,
  navigate,
  userEmail,
  userPhone,
}) {
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const [otpError, setOtpError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resend, setResend] = useState(false);
  const [errorType, setErrorType] = useState(null);
  const [otpResent, setOtpResent] = useState(false);
  const [currentDate] = useState(Date.now());

  const resetOtpState = useCallback(() => {
    setOtpDigits(["", "", "", ""]);
    setOtpError(false);
    setOtpResent(false);
    setErrorType(null);
  }, []);

  const completeOtp = useMemo(() => otpDigits.join(""), [otpDigits]);

  const handleProceed = useCallback(
    (otp = completeOtp) => {
      if (!response || otp.length !== 4) {
        setIsLoading(false);
        return;
      }

      verifyOTP(encipherencode, response.unique_id, otp)
        .then((resp) => {
          sessionStorage.setItem("access_token", resp.data.access_token);
          clearRememberedTrackingAuth();
          setIsLoading(false);
          const storedQuery =
            sessionStorage.getItem("initialQueryParams") || "";
          const storedParams = new URLSearchParams(storedQuery);
          const mergedParams = new URLSearchParams(params.toString());

          storedParams.forEach((value, key) => {
            if (!mergedParams.has(key)) {
              mergedParams.set(key, value);
            }
          });
          mergedParams.delete("auth_key");
          let url = versionedPath(
            getPostLoginPath(mergedParams),
            window.location.pathname,
          );
          const queryString = mergedParams.toString();
          if (queryString) {
            url += `?${queryString}`;
          }
          navigate(url);
          // sessionIframe removed intentionally; legacy session sync disabled.
          // const iframe = document.getElementById("sessionIframe");
          // iframe.contentWindow.postMessage(
          //   {
          //     phone: response.phonenumber,
          //     email: response.email,
          //     topic: "PRAGMA",
          //   },
          //   "*"
          // );
        })
        .catch((err) => {
          setIsLoading(false);
          setOtpError(true);
          setErrorType(getApiErrorMessage(err, i18n.t("login.otpGenericError")));
        });
    },
    [completeOtp, encipherencode, navigate, params, response]
  );

  const handleOtpComplete = useCallback(
    (digits) => {
      setOtpDigits(digits);
      setIsLoading(true);
      handleProceed(digits.join(""));
    },
    [handleProceed]
  );

  const handleResend = useCallback(() => {
    if (response && response !== null) {
      resetOtpState();
      sendOTPRequest(userEmail, encipherencode, userPhone)
        .then((resp) => {
          setOtpResent(true);
          setResponse(resp.data);
          setErrorType(i18n.t("login.otpResent"));
          setResend(false);
        })
        .catch((err) => {
          setOtpError(true);
          setErrorType(getApiErrorMessage(err, i18n.t("login.otpResendError")));
        });
    }
  }, [
    encipherencode,
    resetOtpState,
    response,
    setResponse,
    userEmail,
    userPhone,
  ]);

  const markResendAvailable = useCallback(() => {
    setResend(true);
  }, []);

  const renderer = useCallback(({ minutes, seconds, completed }) => {
    const formattedMinutes = minutes.toString().padStart(2, "0");
    const formattedSeconds = seconds.toString().padStart(2, "0");

    if (completed) {
      return null;
    }

    return formattedMinutes + " : " + formattedSeconds;
  }, []);

  return {
    otpDigits,
    setOtpDigits,
    otpError,
    isLoading,
    resend,
    errorType,
    otpResent,
    currentDate,
    handleOtpComplete,
    handleResend,
    markResendAvailable,
    renderer,
  };
}

export default useOtpFlow;
